import React, { useContext } from 'react';
import { List, ListHeader, DataColumn, LinkColumn } from 'components/lists';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { PageContent } from 'components/layouts/pageContent';
import { ListLayout } from 'components/layouts/listLayout';
import { useFind } from 'hooks';
import { defaultLanguageCodes } from 'views/restaurants/general';
import { getFullDateInCustomTimezone } from 'utils/dateManagement';
import { files } from 'codemash';
import { useInterval } from 'hooks/useInterval';
import { apiActionChooser } from 'utils/apiActionChooser';
import { getFileUrlByFileId } from 'api/client/requests';

export const ProductsImportExport = () => {
  const { state: auth } = useContext(AuthContext);
  const { deleteById } = useContext(CompanyContext);
  const navigate = useNavigate();

  const requestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_SHEET_IMPORTS,
    filter: `{ "company": ObjectId('${auth.company?.company}') }`,
    cultureCode: Config.DEFAULTS.LANGUAGE,
    loadOnInit: true,
    resourceV2: 'AdminProductSheetImport',
    companyIdForV2: auth.company?.company,
  };

  const [findImports, imports, isLoading] = useFind(requestData);

  useInterval(
    () => {
      findImports(null, true);
    },
    15000,
    false,
  );

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCT_SHEET_IMPORTS,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminProductSheetImport',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        findImports(null, true);
      },
    );
  };

  const downloadFile = async (e, fileInfo) => {
    e.preventDefault();

    try {
      let urlResponse;

      await apiActionChooser(
        auth.company?.company,
        async () => {
          urlResponse = await files.getFileUrl({
            secretKey: auth.token,
            fileId: fileInfo.id,
          });
        },
        async () => {
          urlResponse = await getFileUrlByFileId({
            fileId: fileInfo.id,
          });
        },
      );

      if (urlResponse) {
        const urlToFile = decodeURI(urlResponse.result);

        window.open(urlToFile, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <ListLayout isLoading={isLoading === 1}>
      <PageTitle
        title="Products Import / Export"
        breadcrumbs={[{ path: '/products', title: 'Products' }]}
        isTopLevel
        hideMobile
      />
      <PageContent>
        <ListHeader title="Imports / Exports" description="Import or export company products">
          <button
            type="button"
            onClick={() => navigate('/products/imports/add-new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List keyField="_id" source={imports}>
          <DataColumn
            name="Date (UTC +00:00)"
            dbColumnName="date"
            render={(item) => getFullDateInCustomTimezone(item._meta.createdOn)}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Language"
            dbColumnName="language_code"
            render={(item) => {
              if (!item.language_code) {
                return '';
              }

              const found = defaultLanguageCodes.find((x) => x.value === item.language_code);

              if (found) {
                return found.label;
              }

              return '';
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Is Import"
            dbColumnName="is_import"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Is Completed"
            dbColumnName="completed"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Error"
            dbColumnName="error"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="File"
            dbColumnName="file"
            render={(item) => {
              if (!item.file || item.file.length === 0) {
                return null;
              }

              const exportImportFile = item.file[0];

              return (
                <div className="flex-shrink-1">
                  <a
                    href="/"
                    onClick={(e) => downloadFile(e, exportImportFile)}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Download
                  </a>
                </div>
              );
            }}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
